import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, ShieldCheck, Sparkles, ChevronRight, ArrowRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const BrandValue = ({
  icon: Icon,
  title,
  description,
  image
}: {
  icon: any;
  title: string;
  description: string;
  image: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} className="group relative overflow-hidden bg-white" data-id="element-753">
    <div className="aspect-[4/5] overflow-hidden" data-id="element-754">
      <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-755" />
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300" data-id="element-756" />
    </div>
    <div className="absolute inset-0 p-8 flex flex-col justify-end text-white" data-id="element-757">
      <div className="mb-4 p-3 bg-gold/20 backdrop-blur-sm rounded-full w-fit" data-id="element-758">
        <Icon size={24} className="text-gold" data-id="element-759" />
      </div>
      <h3 className="text-2xl font-serif mb-3" data-id="element-760">{title}</h3>
      <p className="text-sm text-gray-200 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-761">
        {description}
      </p>
    </div>
  </motion.div>;
const TimelineItem = ({
  year,
  title,
  description,
  side
}: {
  year: string;
  title: string;
  description: string;
  side: 'left' | 'right';
}) => <div className={`flex w-full mb-12 items-center justify-between ${side === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-762">
    <div className="hidden md:block w-5/12" data-id="element-763" />
    <div className="z-20 flex items-center justify-center w-10 h-10 bg-black border-2 border-gold rounded-full" data-id="element-764">
      <div className="w-2 h-2 bg-gold rounded-full" data-id="element-765" />
    </div>
    <motion.div initial={{
    opacity: 0,
    x: side === 'left' ? -50 : 50
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} className="w-full md:w-5/12 p-6 bg-gray-50 border-l-4 border-gold" data-id="element-766">
      <span className="text-gold font-bold text-sm mb-2 block" data-id="element-767">{year}</span>
      <h4 className="text-lg font-serif mb-2" data-id="element-768">{title}</h4>
      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-769">{description}</p>
    </motion.div>
  </div>;
export function OurStory() {
  useScreenInit();
  return <div className="min-h-screen bg-white" data-id="element-770">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden" data-id="element-771">
        <div className="absolute inset-0" data-id="element-772">
          <img src="https://cdn.magicpatterns.com/uploads/p8EeUmhVdAFUDg1wEiszvE/pasted-image.jpg" className="w-full h-full object-cover" alt="Our Story Hero" data-id="element-773" />
          <div className="absolute inset-0 bg-black/50" data-id="element-774" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white" data-id="element-775">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-776">
            <span className="text-gold uppercase tracking-[0.4em] text-[10px] font-bold block mb-6" data-id="element-777">
              Our Narrative
            </span>
            <h1 className="text-5xl md:text-7xl font-serif mb-8 leading-tight" data-id="element-778">
              Adorned in Culture, <br data-id="element-779" />
              Defined by Style
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed font-light" data-id="element-780">
              TriLyn is more than a brand—it's a celebration of African heritage
              reimagined for the modern world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Mission Section */}
      <section className="py-24 bg-white" data-id="element-781">
        <div className="container mx-auto px-4" data-id="element-782">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-783">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="w-full lg:w-1/2" data-id="element-784">
              <div className="relative" data-id="element-785">
                <img src="https://cdn.magicpatterns.com/uploads/hJK2Ni62uBRFVioU1Qp6bW/pasted-image.jpg" className="w-full aspect-[4/5] object-cover" alt="Cultural Inspiration" data-id="element-786" />
                <div className="absolute -bottom-8 -right-8 w-48 h-48 border-8 border-white shadow-xl hidden md:block" data-id="element-787">
                  <img src="https://cdn.magicpatterns.com/uploads/ok2GGtft3ausR8pfW3rEvG/pasted-image.png" className="w-full h-full object-cover" alt="Detail" data-id="element-788" />
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="w-full lg:w-1/2 space-y-8" data-id="element-789">
              <span className="text-gold uppercase tracking-[0.4em] text-xs font-bold block" data-id="element-790">
                The Mission
              </span>
              <h2 className="text-4xl font-serif leading-tight" data-id="element-791">
                Preserving Heritage Through Modern Craftsmanship
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-792">
                <p data-id="element-793">
                  Our journey began with a simple observation: the incredible
                  artistry of African textiles and jewelry deserved a global
                  stage that respected its roots while embracing contemporary
                  aesthetics.
                </p>
                <p data-id="element-794">
                  At TriLyn, we position modern African fashion as a
                  sophisticated, high-end choice for the global citizen. We
                  bridge the gap between traditional craftsmanship and modern
                  elegance, ensuring that every piece tells a story of identity
                  and pride.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Values Section */}
      <section className="py-24 bg-gray-50" data-id="element-795">
        <div className="container mx-auto px-4" data-id="element-796">
          <div className="text-center mb-16" data-id="element-797">
            <h2 className="text-3xl md:text-4xl font-serif mb-4" data-id="element-798">
              Our Core Values
            </h2>
            <div className="w-20 h-1 bg-gold mx-auto" data-id="element-799"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-800">
            <BrandValue icon={Heart} title="Culture" description="We honor the diverse traditions and artistic expressions of the African continent in every design." image="https://cdn.magicpatterns.com/uploads/p8EeUmhVdAFUDg1wEiszvE/pasted-image.jpg" data-id="element-801" />
            <BrandValue icon={ShieldCheck} title="Craftsmanship" description="Quality is our signature. We partner with skilled artisans to ensure every stitch and bead is perfect." image="https://cdn.magicpatterns.com/uploads/ok2GGtft3ausR8pfW3rEvG/pasted-image.png" data-id="element-802" />
            <BrandValue icon={Sparkles} title="Confidence" description="Our pieces are designed to empower you, providing a unique sense of style that radiates confidence." image="https://cdn.magicpatterns.com/uploads/m5bYg6bwbXzYALrGLnEZcA/pasted-image.png" data-id="element-803" />
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-black text-white overflow-hidden" data-id="element-804">
        <div className="container mx-auto px-4" data-id="element-805">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-806">
            <div className="w-full lg:w-5/12" data-id="element-807">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} className="relative" data-id="element-808">
                <div className="absolute -inset-4 border border-gold/30 translate-x-8 translate-y-8" data-id="element-809" />
                <img src="https://cdn.magicpatterns.com/uploads/14L2vcG3fWeoSEa1LQ9FCn/pasted-image.jpg" className="relative z-10 w-full aspect-[3/4] object-cover transition-all duration-700" alt="Founder" data-id="element-810" />
              </motion.div>
            </div>
            <div className="w-full lg:w-7/12 space-y-8" data-id="element-811">
              <span className="text-gold uppercase tracking-[0.4em] text-xs font-bold block" data-id="element-812">
                Meet Our Owner
              </span>
              <h2 className="text-4xl md:text-5xl font-serif leading-tight" data-id="element-813">
                A Vision for Cultural Excellence
              </h2>
              <div className="space-y-6 text-gray-400 text-lg leading-relaxed" data-id="element-814">
                <p data-id="element-815">
                  "TriLyn was born from my personal desire to wear my heritage
                  with pride in any setting—from the boardroom to a gala. I
                  wanted to create a brand that didn't just sell clothes, but
                  sold a feeling of connection and belonging."
                </p>
                <p data-id="element-816">
                  With a background in design and a lifelong passion for African
                  history, our founder has curated a collection that speaks to
                  the modern woman who values authenticity as much as she values
                  style. Every piece in the TriLyn collection is personally
                  vetted for its cultural significance and quality.
                </p>
              </div>
              <div className="pt-4" data-id="element-817">
                <img src="https://cdn.magicpatterns.com/uploads/pKM35Hxacor8pvdYX3c3hd/pasted-image.png" className="h-16 brightness-0 invert opacity-50" alt="TriLyn Signature" data-id="element-818" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Journey Timeline */}
      <section className="py-24 bg-white" data-id="element-819">
        <div className="container mx-auto px-4" data-id="element-820">
          <div className="text-center mb-20" data-id="element-821">
            <h2 className="text-3xl md:text-4xl font-serif mb-4" data-id="element-822">
              The Brand Journey
            </h2>
            <div className="w-20 h-1 bg-gold mx-auto" data-id="element-823"></div>
          </div>

          <div className="relative wrap overflow-hidden p-10 h-full" data-id="element-824">
            <div className="absolute border-opacity-20 border-gray-700 h-full border left-1/2 hidden md:block" data-id="element-825" />

            <TimelineItem year="2021" title="The Inspiration" description="TriLyn was conceived during a journey across West Africa, inspired by the vibrant markets and skilled artisans of Ghana and Nigeria." side="left" data-id="element-826" />
            <TimelineItem year="2022" title="Official Launch" description="We launched our first collection, featuring signature handcrafted jewelry and limited-edition apparel that quickly gained a loyal following." side="right" data-id="element-827" />
            <TimelineItem year="2023" title="Community Growth" description="Expansion into accessories and the launch of our 'TriLyn Circle' community, connecting heritage lovers globally." side="left" data-id="element-828" />
            <TimelineItem year="2024" title="Global Presence" description="TriLyn pieces are now worn in over 20 countries, bringing African elegance to international stages." side="right" data-id="element-829" />
            <TimelineItem year="2025 & Beyond" title="Future Collaborations" description="Upcoming partnerships with global designers to further push the boundaries of modern African fashion." side="left" data-id="element-830" />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gold text-black" data-id="element-831">
        <div className="container mx-auto px-4 text-center" data-id="element-832">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-833">
            <h2 className="text-4xl md:text-5xl font-serif mb-8" data-id="element-834">
              Experience the Culture Through Style
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-835">
              <Link to="/shop" data-id="element-836">
                <button className="bg-black text-white px-10 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-all duration-300 flex items-center justify-center gap-3 w-full sm:w-auto" data-id="element-837">
                  Shop the Collection <ArrowRight size={18} data-id="element-838" />
                </button>
              </Link>
              <button className="border-2 border-black text-black px-10 py-4 text-sm uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-all duration-300 w-full sm:w-auto" data-id="element-839">
                View Colabs
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}